<script>
  // A dataset's small-number rules, changed after seeing what the change would
  // hide in each of its cubes (docs/ref-belonging-plan.md, step 7.6). Saving
  // counts the cubes again; what partners hold changes only when a manager
  // sends them an updated copy.
  //
  //   dataset  the dataset page's entry (sharing.suppressBelow, minPerColumn)
  //   name     a cube's id → what it counts, in the dataset's words
  //   onsaved  called once saved, to read the page again
  import Button from './Button.svelte';
  import Alert from './Alert.svelte';
  import { previewRules, saveRules } from '../lib/api.js';

  let { dataset, name, onsaved } = $props();

  const unit = $derived(dataset.unit ?? 'records');
  let editing = $state(false);
  let suppressBelow = $state(dataset.sharing.suppressBelow);
  let wholeColumns = $state(dataset.sharing.minPerColumn !== null);
  let minPerColumn = $state(dataset.sharing.minPerColumn ?? 10);
  const rules = $derived({ suppressBelow: Number(suppressBelow), minPerColumn: wholeColumns ? Number(minPerColumn) : null });
  const isCount = (n) => Number.isInteger(n) && n >= 0;
  const problem = $derived(!isCount(rules.suppressBelow) ? 'Give the smallest count shown as a whole number, 0 or more.'
    : rules.minPerColumn !== null && !isCount(rules.minPerColumn) ? 'Give the number of records as a whole number, 0 or more.' : null);
  const unchanged = $derived(rules.suppressBelow === dataset.sharing.suppressBelow
    && rules.minPerColumn === dataset.sharing.minPerColumn);

  // The preview is of one exact set of rules; changing them again asks for
  // another before saving.
  let preview = $state(null);
  let previewed = $state(null);
  const fresh = $derived(preview && previewed === JSON.stringify(rules));
  let busy = $state(null);
  let error = $state(null);

  async function look() {
    busy = 'Counting…'; error = null;
    try { preview = await previewRules(dataset.id, rules); previewed = JSON.stringify(rules); }
    catch (e) { error = e.message; }
    finally { busy = null; }
  }
  async function save() {
    busy = 'Saving and counting the cubes again…'; error = null;
    try { await saveRules(dataset.id, rules); editing = false; preview = null; onsaved?.(); }
    catch (e) { error = e.message; }
    finally { busy = null; }
  }
  function cancel() {
    editing = false; preview = null; error = null;
    suppressBelow = dataset.sharing.suppressBelow;
    wholeColumns = dataset.sharing.minPerColumn !== null;
    minPerColumn = dataset.sharing.minPerColumn ?? 10;
  }

  // One cube under one set of rules, in a line.
  function shown(s) {
    if (!s) return '—';
    if (s.minPerColumn != null) {
      const withheld = s.periods.filter((p) => p.withheld);
      if (!s.periods.length) return 'withheld: no values';
      return withheld.length ? `withheld for ${withheld.map((p) => `${p.period ?? 'all'} (${p.records})`).join(', ')}`
        : 'shared whole';
    }
    return s.hidden ? `${s.published} of ${s.possible} counts shown, ${s.hidden} hidden` : `all ${s.possible} counts shown`;
  }
  const differs = (c) => shown(c.now) !== shown(c.next);
</script>

{#if !editing}
  <Button variant="secondary" onclick={() => (editing = true)}>Change the rules</Button>
{:else}
  <div class="rules">
    <label class="wf2-checkline">Hide any count under
      <input class="wf2-control" type="number" min="0" style="width:5em" bind:value={suppressBelow} aria-label="Hide any count under" /></label>
    <label class="wf2-checkline"><input type="checkbox" class="wf2-checkbox" bind:checked={wholeColumns} />
      Share a term on its own whole, small counts included, when at least</label>
    <div class="wf2-row" style="gap:8px; align-items:center; padding-left:1.6em">
      <input class="wf2-control" type="number" min="0" style="width:5em" bind:value={minPerColumn} disabled={!wholeColumns} aria-label="Records needed" />
      <span class="wf2-text">{unit} have a value for it in that period; with fewer, the period is withheld.</span>
    </div>
    <p class="wf2-hint">{wholeColumns ? 'Where two terms are combined, counts under the first number are still hidden.'
      : 'Unticked, a term on its own hides small counts like a combination does.'}</p>
  </div>
  {#if problem}<p class="wf2-hint">{problem}</p>{/if}
  {#if fresh}
    {@const changed = preview.cubes.filter(differs)}
    <div class="wf2-tablewrap"><table class="wf2-table">
      <thead><tr><th>Cube</th><th>Now</th><th>With the change</th></tr></thead>
      <tbody>
        <!-- The cubes that change first: with a survey's dozens, they are what to read. -->
        {#each [...changed, ...preview.cubes.filter((c) => !differs(c))] as c (c.id)}
          <tr class:same={!differs(c)}><td>{name(c.id)}<span class="col-stats mono">{c.id}</span></td><td>{shown(c.now)}</td><td>{shown(c.next)}</td></tr>
        {/each}
      </tbody>
    </table></div>
    <p class="wf2-caption">{changed.length ? `${changed.length} of ${preview.cubes.length} cubes change.` : 'No cube changes.'}
      Saving counts them again in your Pod; partners keep the copy they have until a manager sends an updated one.</p>
  {/if}
  {#if error}<Alert title="Could not change the rules" variant="danger"><p>{error}</p></Alert>{/if}
  <div class="wf2-row" style="gap:12px; align-items:center">
    <Button variant="secondary" onclick={look} disabled={Boolean(busy) || Boolean(problem) || unchanged}>See what it changes</Button>
    <Button variant="primary" onclick={save} disabled={Boolean(busy) || !fresh}>Save the rules</Button>
    <Button variant="ghost" onclick={cancel} disabled={Boolean(busy)}>Cancel</Button>
    {#if busy}<span class="wf2-hint">{busy}</span>{/if}
  </div>
{/if}

<style>
  .rules { display: grid; gap: 6px; margin-bottom: var(--space-2); }
  .same td { color: var(--muted); }
</style>
